import { useCallback, useState } from 'react';
import {
  clampColumnWidth,
  columnWidthKey,
  MIN_NARROW_COLUMN_WIDTH,
  readColumnWidths,
  writeColumnWidths,
  type ColumnWidths,
} from './column-widths';

export interface ColumnWidthControls {
  /** The remembered width, or the column's own default when nobody has set one. */
  widthOf: (level: string, fieldKey: string, fallback: number) => number;
  setWidth: (level: string, fieldKey: string, width: number) => void;
  /** Puts one column back to its default, on a double-click of the handle. */
  resetWidth: (level: string, fieldKey: string) => void;
}

/**
 * Remembered column widths for one platform's sheet.
 *
 * Read once into state rather than on every render: this is dragged at pointer
 * rate, and going through `localStorage` per frame would make the drag stutter.
 * The write is where it lands, which is cheap enough at one per move event and
 * means a refresh mid-drag keeps what was there.
 */
export function useColumnWidths(platform: string): ColumnWidthControls {
  const [widths, setWidths] = useState<ColumnWidths>(readColumnWidths);

  const update = useCallback((mutate: (current: ColumnWidths) => ColumnWidths) => {
    setWidths((current) => {
      const next = mutate(current);
      writeColumnWidths(next);
      return next;
    });
  }, []);

  const widthOf = useCallback(
    (level: string, fieldKey: string, fallback: number) =>
      widths[columnWidthKey(platform, level, fieldKey)] ?? fallback,
    [platform, widths],
  );

  const setWidth = useCallback(
    (level: string, fieldKey: string, width: number) =>
      update((current) => ({
        ...current,
        // Already clamped by the handle against that column's own floor; this only
        // guards a value arriving from anywhere else.
        [columnWidthKey(platform, level, fieldKey)]: clampColumnWidth(
          width,
          MIN_NARROW_COLUMN_WIDTH,
        ),
      })),
    [platform, update],
  );

  const resetWidth = useCallback(
    (level: string, fieldKey: string) =>
      update((current) => {
        // Rebuilt without the key rather than deleted from a copy: the column falls
        // back to its schema width only when the entry is absent, so an `undefined`
        // left behind would still read as a stored preference.
        const target = columnWidthKey(platform, level, fieldKey);
        return Object.fromEntries(Object.entries(current).filter(([key]) => key !== target));
      }),
    [platform, update],
  );

  return { widthOf, setWidth, resetWidth };
}
